const styles = getComputedStyle(document.body);

const varStartRegex = /^var\(/u;
const varEndRegex = /\)$/u;

export function debounce(fn, time) {
  let timeout;

  return function () {
    const functionCall = () => fn.apply(this, arguments);

    clearTimeout(timeout);
    timeout = setTimeout(functionCall, time);
  };
}

export function debounceAF(callback) {
  let newFrame;

  return (...args) => {
    let result;

    if (newFrame) cancelAnimationFrame(newFrame);

    newFrame = window.requestAnimationFrame(() => {
      result = callback(...args);
    });

    return result;
  };
}

function getThemeElement(themeName = "ddhq") {
  return document.getElementById(themeName) || null;
}

function getStrippedCSSVarName(fullVarName) {
  const stripped = fullVarName.substring(
    fullVarName.lastIndexOf("(") + 1,
    fullVarName.lastIndexOf(")")
  );

  const invalid = !stripped || typeof stripped !== "string" || !stripped.startsWith("--");

  return invalid ? null : stripped;
}

function getStyle(element, propertyName) {
  const style = getComputedStyle(element).getPropertyValue(propertyName);

  const invalid = !style || typeof style !== "string" || style.length === 0;

  return invalid ? null : style;
}

export const ddhqTheme = {
  "--primary-color": "#222222",
  "--secondary-color": "#666666",
  "--dem-color": "#4575A1",
  "--gop-color": "#D84B44",
  "--gre-color": "#57AEA6",
  "--lib-color": "#FFBA49",
  "--prog-color": "#D24646",
  "--ind-color": "#854c79",
  "--uu-color": "#A34333",
  "--oth-color": "#cc55ab",
};

const businessInsiderTheme = {
  ...ddhqTheme,
  "--dem-color": "#66abcf",
  "--gop-color": "#fa3138",
};

export function stripSurroundingVar(name) {
  return name.replace(varStartRegex, "").replace(varEndRegex, "");
}

export function isVar(value) {
  const varStr = value.substring(0, 6);

  if (varStr === "var(--") return true;

  const hyphenStr = value.substring(0, 1);

  if (hyphenStr === "--") return true;

  return false;
}

export function hexOrVarToHex(str) {
  if (str.startsWith("#")) {
    return str;
  }

  if (str in ddhqTheme) {
    return ddhqTheme[str];
  }

  if (isVar(str)) {
    const strippedName = stripSurroundingVar(str);
    const value = styles.getPropertyValue(strippedName);

    if (value) {
      return value;
    }
  }

  return "#000";
}

// fallback function for when getCSSVarHex fails
// in some instances, getComputedStyle may fail to resolve a css property
// for these cases, we fall back to a simple dictionary lookup
//
// getComputedStyle may not be reliable enough to use in production
// instead, we should use a js dictionary and, to keep things dry,
// we should render the css theme at build/runtime from the js definition
function getCSSVarHexFallback(theme = "ddhq", varName) {
  const themeInstance = theme === "business-insider" ? businessInsiderTheme : ddhqTheme;

  return themeInstance[varName] || "#fff";
}

export function getCSSVarHex(theme, str) {
  const varName = getStrippedCSSVarName(str);

  if (varName === null) {
    console.warn("DDHQ: Unable to strip CSS variable name");
    return "#fff";
  }

  const themeElement = getThemeElement(theme);

  if (themeElement === null) {
    console.warn("DDHQ: Unable to locate theme root element");
    return "#fff";
  }

  const cssValue = getStyle(themeElement, varName);

  if (cssValue === null) {
    return getCSSVarHexFallback(theme, varName);
  }

  return cssValue.toUpperCase();
}

export function hexToRGB(hexValue, alpha) {
  if (!hexValue) return;
  const [r, g, b] = hexValue.match(/\w\w/g).map((x) => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
}

export function getGeoType(state) {
  if (
    ["Maine", "Massachusetts", "New Hampshire", "Vermont", "Connecticut", "Rhode Island"].includes(
      state
    )
  ) {
    return "vcu";
  }
  return "county";
}
